import React from 'react';
import { motion } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import { useGame } from '../../contexts/GameContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { TYPE_EFFECTS } from '../../constants/specialFish';
import SafeIcon from '../common/SafeIcon';
import TypeIcon from '../ui/TypeIcon';

const TypeSelectorModal = ({ onClose }) => {
  const { state, changeType } = useGame();
  const { t } = useLanguage();
  const types = Object.entries(TYPE_EFFECTS).map(([id, config]) => ({
    id,
    ...config,
    name: t(`types.${id}.name`) || config.name,
    description: t(`types.${id}.description`) || config.description,
  }));

  const handleTypeSelect = (typeId) => {
    changeType(typeId);
  };

  return (
    <div className="type-selector-modal-content">
      {/* Header */}
      <div className="modal-header-sticky">
        <div>
          <h3 className="text-2xl font-bold text-amber-800 flex items-center gap-2">
            <span className="text-3xl">🎭</span>
            {t('game.selectType') || '選擇祈福類型'}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {t('game.selectTypeDesc') || '選擇不同類型獲得不同祝福效果'}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label={t('common.close')}
        >
          <SafeIcon icon={FiX} className="text-xl" />
        </button>
      </div>

      {/* 類型卡片網格 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {types.map((type, index) => {
          const isActive = state.currentType === type.id;

          return (
            <motion.button
              key={type.id}
              className={`type-selector-card ${isActive ? 'active' : ''}`}
              style={{
                '--type-color': type.color,
                '--type-glow': type.glowColor,
              }}
              onClick={() => handleTypeSelect(type.id)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: index * 0.05 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              {/* 背景漸層 */}
              <div
                className="type-selector-bg"
                style={{ background: type.glowColor }}
              />

              {/* 內容 */}
              <div className="type-selector-content">
                <div className="type-selector-icon">
                  <TypeIcon type={type.id} size={48} />
                </div>
                <div className="type-selector-info">
                  <h4
                    className="type-selector-name"
                    style={{ color: type.color }}
                  >
                    {type.name}
                  </h4>
                  <p className="type-selector-description">
                    {type.description}
                  </p>
                </div>

                {/* 粒子 */}
                <div className="type-selector-particles">
                  {type.particles.slice(0, 2).map((particle, particleIndex) => (
                    <span
                      key={particleIndex}
                      className="type-selector-particle type-particle-float"
                      style={{ animationDelay: `${particleIndex * 0.3}s` }}
                    >
                      {particle}
                    </span>
                  ))}
                </div>

                {/* 選中指示器 */}
                {isActive && (
                  <motion.div
                    className="type-selector-check"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 20,
                    }}
                  >
                    <span className="text-white font-bold">✓</span>
                  </motion.div>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* 提示資訊 */}
      <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
        <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
          <span>💡</span>
          <span>{t('game.typeTips') || '類型小提示'}</span>
        </h4>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• {t('game.typeTip1') || '不同類型有不同的粒子特效'}</li>
          <li>• {t('game.typeTip2') || '切換類型不會影響連擊和統計'}</li>
          <li>• {t('game.typeTip3') || '各類型的敲擊數會分別計算'}</li>
        </ul>
      </div>

      {/* 關閉按鈕 */}
      <div className="mt-6 flex justify-center">
        <motion.button
          className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl shadow-md transition-colors"
          onClick={onClose}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {t('common.confirm') || '確定'}
        </motion.button>
      </div>
    </div>
  );
};

export default TypeSelectorModal;